import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSyncAlt,
  faRotateRight,
  faFilter,
  faSpinner,
  faFileCsv,
  faFileExcel,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { BASE_URL } from "../../config";

const statusOptions = ["queued", "processing", "success", "failed"];

const ClaimsUploadQueue = () => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [summary, setSummary] = useState({
    queued: 0,
    processing: 0,
    success: 0,
    failed: 0,
  });
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    status: "",
    batch_no: "",
    transaction_no: "",
    upload_id: "",
    user: "",
    file_name: "",
  });

  const token = localStorage.getItem("authentication/token");

  const fetchQueue = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${BASE_URL}/claims/upload-queue`, {
        params: {
          page,
          per_page: perPage,
          sort_by: sortBy,
          sort_direction: sortDir,
          ...filters,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setRows(response.data?.data || []);
      if (response.data?.summary) {
        setSummary(response.data.summary);
      }
      const pagination = response.data?.pagination || {};
      setTotalPages(pagination.total_pages || 1);
      setTotalRecords(pagination.total_records || 0);
    } catch (error) {
      // silent fail; page-level toasts optional
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQueue();
  }, [page, perPage, sortBy, sortDir]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchQueue();
    }, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, page, perPage, sortBy, sortDir, filters]);

  const applyFilters = () => {
    setPage(1);
    fetchQueue();
  };

  const resetFilters = () => {
    setFilters({
      date_from: "",
      date_to: "",
      status: "",
      batch_no: "",
      transaction_no: "",
      upload_id: "",
      user: "",
      file_name: "",
    });
    setPage(1);
  };

  useEffect(() => {
    fetchQueue();
  }, [filters]);

  const toggleSelect = (uploadId) => {
    setSelected((prev) =>
      prev.includes(uploadId)
        ? prev.filter((id) => id !== uploadId)
        : [...prev, uploadId]
    );
  };

  const selectAll = () => {
    const retryable = rows.filter(
      (r) => r.status === "failed" || r.status === "queued"
    );
    if (retryable.length === 0) return;
    if (selected.length === retryable.length) {
      setSelected([]);
      return;
    }
    setSelected(retryable.map((r) => r.upload_id));
  };

  const retrySingle = async (uploadId) => {
    await axios.post(
      `${BASE_URL}/claims/upload-queue/retry/${uploadId}`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    fetchQueue();
  };

  const retryBulk = async () => {
    if (selected.length === 0) return;
    await axios.post(
      `${BASE_URL}/claims/upload-queue/retry-bulk`,
      { upload_ids: selected },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    setSelected([]);
    fetchQueue();
  };

  const exportQueue = async (format) => {
    const response = await axios.get(`${BASE_URL}/claims/upload-queue/export`, {
      params: { format, ...filters },
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: "blob",
    });

    const blob = new Blob([response.data], {
      type:
        format === "excel"
          ? "application/vnd.ms-excel"
          : "text/csv;charset=utf-8;",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().replace(/[:T]/g, "-").split(".")[0];
    link.href = url;
    link.setAttribute(
      "download",
      `claims_upload_queue_loa_${timestamp}.${format === "excel" ? "xls" : "csv"}`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const statusBadge = (status) => {
    const base = "px-2 py-1 rounded-full text-xs";
    if (status === "success") return `${base} bg-green-100 text-green-700`;
    if (status === "failed") return `${base} bg-red-100 text-red-700`;
    if (status === "processing") return `${base} bg-blue-100 text-blue-700`;
    return `${base} bg-yellow-100 text-yellow-700`;
  };

  const showingText = useMemo(() => {
    if (!totalRecords) return "No records";
    const from = (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, totalRecords);
    return `Showing ${from} to ${to} of ${totalRecords}`;
  }, [page, perPage, totalRecords]);

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Claims Upload Queue - LOA</h1>
          <p className="text-sm text-gray-600">
            Monitor LOA uploads and retry failed jobs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchQueue}
            className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <FontAwesomeIcon icon={faSyncAlt} className="mr-2" /> Refresh
          </button>
          <button
            onClick={() => exportQueue("csv")}
            className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <FontAwesomeIcon icon={faFileCsv} className="mr-2" /> CSV
          </button>
          <button
            onClick={() => exportQueue("excel")}
            className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <FontAwesomeIcon icon={faFileExcel} className="mr-2" /> Excel
          </button>
          <button
            onClick={retryBulk}
            disabled={selected.length === 0}
            className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faRotateRight} className="mr-2" /> Retry Selected
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs text-gray-500">Queued</div>
          <div className="text-2xl font-semibold text-yellow-600">{summary.queued}</div>
          <div className="text-xs text-gray-500 mt-1">Waiting</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs text-gray-500">Processing</div>
          <div className="text-2xl font-semibold text-blue-600">{summary.processing}</div>
          <div className="text-xs text-gray-500 mt-1">In progress</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs text-gray-500">Success</div>
          <div className="text-2xl font-semibold text-green-600">{summary.success}</div>
          <div className="text-xs text-gray-500 mt-1">Completed</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs text-gray-500">Failed</div>
          <div className="text-2xl font-semibold text-red-600">{summary.failed}</div>
          <div className="text-xs text-gray-500 mt-1">Needs retry</div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setAutoRefresh((prev) => !prev)}
          className={`px-3 py-2 text-sm rounded-lg border ${
            autoRefresh
              ? "bg-emerald-600 text-white border-emerald-600"
              : "bg-white border-gray-200"
          }`}
        >
          <FontAwesomeIcon icon={faClock} className="mr-2" />
          Auto-refresh {autoRefresh ? "On" : "Off"} (10s)
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-600">Date From</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters((prev) => ({ ...prev, date_from: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Date To</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters((prev) => ({ ...prev, date_to: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600">Batch No</label>
            <input
              value={filters.batch_no}
              onChange={(e) => setFilters((prev) => ({ ...prev, batch_no: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Batch no"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Transaction No</label>
            <input
              value={filters.transaction_no}
              onChange={(e) => setFilters((prev) => ({ ...prev, transaction_no: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Transaction no"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Upload ID</label>
            <input
              value={filters.upload_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, upload_id: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Upload id"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">User</label>
            <input
              value={filters.user}
              onChange={(e) => setFilters((prev) => ({ ...prev, user: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Username or name"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">File Name</label>
            <input
              value={filters.file_name}
              onChange={(e) => setFilters((prev) => ({ ...prev, file_name: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Original file"
            />
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={applyFilters}
            className="px-3 py-2 text-sm bg-gray-900 text-white rounded-lg"
          >
            <FontAwesomeIcon icon={faFilter} className="mr-2" /> Apply Filters
          </button>
          <button
            onClick={resetFilters}
            className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-3 text-sm text-gray-600 flex items-center justify-between">
          <span>{showingText}</span>
          <div className="flex items-center gap-2">
            <span>Per page</span>
            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className="border border-gray-200 rounded-lg px-2 py-1"
            >
              {[5, 10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="p-3 text-left">
                  <input
                    type="checkbox"
                    checked={selected.length > 0 && selected.length === rows.length}
                    onChange={selectAll}
                  />
                </th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Upload ID</th>
                <th className="p-3 text-left">User</th>
                <th className="p-3 text-left">Batch No</th>
                <th className="p-3 text-left">Transaction No</th>
                <th className="p-3 text-left">File</th>
                <th className="p-3 text-left">Created</th>
                <th className="p-3 text-left">Error</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading ? (
                <tr>
                  <td colSpan="10" className="p-4 text-center text-gray-500">
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="p-4 text-center text-gray-500">
                    No upload queue records found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.upload_id}>
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selected.includes(row.upload_id)}
                        onChange={() => toggleSelect(row.upload_id)}
                        disabled={
                          row.status !== "failed" && row.status !== "queued"
                        }
                      />
                    </td>
                    <td className="p-3">
                      <span className={statusBadge(row.status)}>
                        {row.status}
                      </span>
                    </td>
                    <td className="p-3 break-all">{row.upload_id}</td>
                    <td className="p-3">
                      <div className="text-gray-900">
                        {row.username || row.user_id}
                      </div>
                      <div className="text-xs text-gray-500">
                        {[row.first_name, row.last_name].filter(Boolean).join(" ")}
                      </div>
                    </td>
                    <td className="p-3">{row.batch_no || "-"}</td>
                    <td className="p-3">{row.transaction_no}</td>
                    <td className="p-3 break-words">
                      {row.original_file_name || row.file_name}
                    </td>
                    <td className="p-3">{row.created_at}</td>
                    <td className="p-3 text-xs text-red-600 break-words">
                      {row.error_message || "-"}
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => retrySingle(row.upload_id)}
                        className="px-2 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                        disabled={
                          row.status !== "failed" && row.status !== "queued"
                        }
                      >
                        Retry
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="p-3 flex items-center justify-between text-sm text-gray-600">
          <button
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-50"
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClaimsUploadQueue;
